import { PathEditor } from '../curves/PathEditor.js';
import { applyEdits, tidy } from '../curves/curve-math.js';
import type { ShapeEditorProps } from './doc-context.js';
import { arr, Bool, bool, Num, num, patch, Section } from './fields.js';
import { points3, ProfileSourceFields, resolvedProfile } from './profile-source.js';

/** Rough size of a profile (for the path view's circles). */
function profileExtent(shape: Record<string, unknown>): number {
  const p = resolvedProfile(shape, 16, false, num(shape.radius) ?? 0.1);
  return p ? p.reduce((m, q) => Math.max(m, Math.hypot(q[0], q[1])), 0) : 0.1;
}

/** `sweep`: a 2D profile swept along a 3D path with per-point scale and a twist. */
export function SweepEditor({ shape, onChange }: ShapeEditorProps) {
  const set = (key: string, v: unknown) => onChange(patch(shape, key, v));
  const path = points3(shape.path);
  const scale = arr<unknown>(shape.scale).map((s) => (typeof s === 'number' ? s : 1));
  const extent = profileExtent(shape);
  const segments = num(shape.segments) ?? 16;
  const setScale = (i: number, v: number) => {
    const next = path.map((_, j) => scale[j] ?? scale[scale.length - 1] ?? 1);
    next[i] = v;
    set('scale', next.every((s) => s === 1) ? [] : next.map(tidy));
  };
  return (
    <>
      <Section title="Profile" hint="Closed 2D profile [x, y]: x is sideways, y is 'up' along the path normal.">
        <ProfileSourceFields
          value={shape}
          onChange={(next) => onChange({ ...next, type: shape.type })}
          segments={segments}
          smooth={bool(shape.smoothProfile) ?? false}
          axes={['X (side)', 'Y (up)']}
          defaultRadius={0.1}
        />
        <Num label="Segments" hint="Points generated for ellipse / circle / rect profiles." integer min={3} value={num(shape.segments)} fallback={16} onChange={(v) => set('segments', v)} />
        <Bool label="Smooth profile" hint="Smooth closed curve through the profile points." value={bool(shape.smoothProfile)} onChange={(v) => set('smoothProfile', v)} />
      </Section>
      <Section title="Path" hint="Path points (part space). Select a point to set its profile scale.">
        <PathEditor
          points={path}
          closed={bool(shape.closed) ?? false}
          smooth={bool(shape.smooth) ?? false}
          samples={num(shape.samples) ?? 6}
          radii={path.map((_, i) => extent * (scale.length ? (scale[Math.min(i, scale.length - 1)] ?? 1) : 1))}
          onChange={(p, edits) => {
            const next = patch(shape, 'path', p);
            if (scale.length) {
              next.scale = applyEdits(scale, edits, (i, s) => tidy(((s[i - 1] ?? s[i] ?? 1) + (s[i] ?? s[i - 1] ?? 1)) / 2));
            }
            onChange(next);
          }}
          renderPoint={(i) => (
            <Num
              label="Scale"
              hint="Profile scale at this path point (interpolated between points)."
              min={0}
              step={0.05}
              value={scale.length ? (scale[Math.min(i, scale.length - 1)] ?? 1) : 1}
              onChange={(v) => setScale(i, v ?? 1)}
            />
          )}
        />
      </Section>
      <Num label="Twist" hint="Total twist in degrees from the first to the last path point." step={1} value={num(shape.twist)} fallback={0} onChange={(v) => set('twist', v)} />
      {scale.length > 0 && (
        <Bool label="Per-point scale" hint={`Profile scale at each path point (${scale.length} values); off = 1 everywhere.`} value onChange={(on) => !on && set('scale', [])} />
      )}
      <Bool label="Closed" hint="Connect the last path point back to the first (a ring)." value={bool(shape.closed)} onChange={(v) => set('closed', v)} />
      <Bool label="Caps" hint="Close the open ends." value={bool(shape.caps)} fallback onChange={(v) => set('caps', v)} />
      <Bool label="Smooth" hint="Smooth curve through the path points." value={bool(shape.smooth)} onChange={(v) => set('smooth', v)} />
      <Num label="Samples" hint="Curve samples per path span when smooth." integer min={1} max={64} value={num(shape.samples)} fallback={6} onChange={(v) => set('samples', v)} />
    </>
  );
}
